import {
    AfterViewChecked,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    signal,
    untracked,
    ViewChild,
    viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    CompanyController,
    DocGenPdfStatusEnum,
    DocumentConfigPartial,
    DocumentConfigScopeEnum,
    DocumentItemTypeEnum,
    DocumentTypeEnum,
    OrderEntity,
    SharedWithPartial,
    SignedByPartial,
} from '@domain';
import { environment } from '@environments';
import { catchError, finalize, of, switchMap } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaDialogConfirmationService, RaToastService } from '@shared/components';
import { RaAvatarsListComponent, RaLoadingComponent } from '@shared/components';
import { DocumentSectionController } from 'src/app/domain/controllers/document/document-section.controller';
import { RaFullPageLoadingComponent } from 'src/app/shared/components/ra-full-page-loading/ra-full-page-loading.component';
import { RaModalListOrdersComponent } from 'src/app/shared/components/ra-modal-list-orders/ra-modal-list-orders.component';
import { RaSquareIconComponent } from 'src/app/shared/components/ra-square-icon/ra-square-icon.component';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaClickOutsideDirective } from 'src/app/shared/directives';
import {
    ChecklistCompletionPipe,
    computeChecklistSteps,
} from 'src/app/shared/pipes/documents/checklist-completion.pipe';
import { RaEditTooltipDirective } from '../../directives/ra-edit-tooltip/ra-edit-tooltip.directive';
import { GuidedTourService } from 'src/app/shared/services/guided-tour.service';
import {
    downloadFileFromUrl,
    openFileInNewTab,
    pdfFileNameFromTitle,
} from 'src/app/shared/utils/file-download.util';
import { EditDataService } from '../../services/_edit-data.service';
import { PdfPrintService } from '../../services/pdf-print.service';
import { EditService } from '../../services/edit-service';
import { EditModeService } from '../../services/edit-mode.service';
import { AiEditStore } from '../../services/ai-edit.store';
import { FieldRemovalService } from '../../services/field-removal.service';
import { TemplateEditService } from '../../services/template-edit.service';
import { ModalConfigScopeComponent } from '../modals/modal-config-scope/modal-config-scope.component';
import { ModalContractSignersComponent } from '../modals/modal-contract-signers/modal-contract-signers.component';
import { ModalDocumentAccessComponent } from '../modals/modal-document-access/modal-document-access.component';
import { ModalDocumentVisibilityComponent } from '../modals/modal-document-visibility/modal-document-visibility.component';
import { ModalDocumentQrcodeComponent } from '../modals/modal-document-qrcode/modal-document-qrcode.component';
import { ModalCreateTemplateComponent } from '../modals/modal-create-template/modal-create-template.component';
import {
    DocumentConfigFormValue,
    ModalDocumentConfigComponent,
} from '../modals/modal-document-config/modal-document-config.component';
import {
    FooterEditorSaveEvent,
    ModalFooterEditorComponent,
} from '../modals/modal-footer-editor/modal-footer-editor.component';
import {
    HeaderEditorSaveEvent,
    ModalHeaderEditorComponent,
} from '../modals/modal-header-editor/modal-header-editor.component';
import {
    CoverEditorSaveEvent,
    ModalCoverEditorComponent,
} from '../modals/modal-cover-editor/modal-cover-editor.component';
import { ModalListTrashComponent } from '../modals/modal-list-trash/modal-list-trash.component';
import { ModalResponsiblesComponent } from '../modals/modal-responsibles/modal-responsibles.component';
import { DocumentHistoryPanelComponent } from '../document-history-panel/document-history-panel.component';
import { EditToolbarComponent } from '../edit-toolbar/edit-toolbar.component';
import { UsageLimitsPillComponent } from '../usage-limits-pill/usage-limits-pill.component';

@Component({
    selector: 'edit-header',
    templateUrl: 'edit-header.component.html',
    styleUrl: 'edit-header.component.scss',
    imports: [
        RaSquareIconComponent,
        RaIconComponent,
        RaFullPageLoadingComponent,
        RaModalListOrdersComponent,
        RaAvatarsListComponent,
        RaLoadingComponent,
        RaEditTooltipDirective,
        RaClickOutsideDirective,
        ModalListTrashComponent,
        ModalCreateTemplateComponent,
        ModalDocumentConfigComponent,
        ModalFooterEditorComponent,
        ModalHeaderEditorComponent,
        ModalCoverEditorComponent,
        ModalConfigScopeComponent,
        ModalContractSignersComponent,
        ModalDocumentAccessComponent,
        ModalDocumentVisibilityComponent,
        ModalDocumentQrcodeComponent,
        ModalResponsiblesComponent,
        DocumentHistoryPanelComponent,
        EditToolbarComponent,
        UsageLimitsPillComponent,
        ChecklistCompletionPipe,
        TranslatePipe,
    ],
})
export class EditHeaderComponent implements AfterViewChecked {
    @ViewChild('titleInput') titleInputRef?: ElementRef<HTMLInputElement>;
    public showModalListTrash = signal(false);
    public showHistoryDrawer = signal(false);
    public showMoreMenu = signal(false);
    /** Saindo do editor: segura cliques repetidos no voltar enquanto a saída não termina. */
    public leaving = signal(false);
    /** Modal de seleção do order de destino — aberto pela ação "Mover documento". */
    public showModalMoveDocument = signal(false);
    public showModalContractSigners = signal(false);
    public isModalDocumentConfigVisible = signal(false);
    public loadingDocumentConfig = signal(false);
    /** Modal de visibilidade (público/privado) — aberto pelo menu/cadeado. */
    public showModalDocumentVisibility = signal(false);
    /** Modal do QR Code do documento (imprimir/salvar o link do preview). */
    public showModalDocumentQrcode = signal(false);
    /** Modal "Quem pode acessar" (membros com acesso ao documento privado). */
    public showModalDocumentAccess = signal(false);
    public loadingDocumentAccess = signal(false);
    /** Loading do toggle público/privado. */
    public loadingVisibility = signal(false);
    /** Modal de gestão da equipe do documento (qualquer tipo de documento; aberto a todos). */
    public showResponsiblesModal = signal(false);
    /**
     * O usuário mexeu em alguma configuração desde que abriu o modal de definições.
     * Não indica "não salvo" (o auto-save já gravou no documento): habilita o botão
     * Salvar, que pergunta onde mais as alterações devem valer, e faz a saída pedir
     * confirmação. Vive aqui porque é o pai que enxerga também os saves de
     * capa/cabeçalho/rodapé, feitos em modais separados.
     */
    public configHasChanges = signal(false);
    /** Modal que pergunta o alcance das alterações está aberto? */
    public isModalConfigScopeVisible = signal(false);
    /** Loading enquanto o alcance escolhido é propagado. */
    public savingConfigScope = signal(false);

    public showFooterEditor = signal(false);
    public loadingFooter = signal(false);
    /**
     * Abrindo o editor do cabeçalho ou do rodapé: o primeiro acesso semeia o conteúdo
     * padrão na API antes de abrir. Mostra o loading no lápis e segura cliques repetidos.
     */
    public openingEditor = signal<'header' | 'footer' | null>(null);
    /** Baixando o PDF antigo (o arquivo inteiro vem antes de salvar): segura cliques repetidos. */
    public downloading = signal(false);
    /** Ref ao modal de config para desfazer o fechamento quando a guarda é cancelada. */
    public configModalRef = viewChild(ModalDocumentConfigComponent);
    /** Ref ao editor de footer para disparar o fechamento animado ao salvar. */
    public footerEditorRef = viewChild(ModalFooterEditorComponent);
    public showHeaderEditor = signal(false);
    public loadingHeader = signal(false);
    /** Ref ao editor de header para disparar o fechamento animado ao salvar. */
    public headerEditorRef = viewChild(ModalHeaderEditorComponent);
    public showCoverEditor = signal(false);
    public loadingCover = signal(false);
    /** Ref ao editor de capa para disparar o fechamento animado ao salvar. */
    public coverEditorRef = viewChild(ModalCoverEditorComponent);
    public loadingContractSigners = signal(false);
    public isCreatingTemplate = signal(false);
    public isEditingTitle = signal(false);
    public tempTitle = signal('');
    public DocumentTypeEnum = DocumentTypeEnum;
    private shouldFocusInput = signal(false);
    /** `true` enquanto o tour pós-clone ainda não foi mostrado (ver constructor). */
    private _shouldShowClonedTour = signal(false);
    private _clonedTourChecked = false;

    // * [Injects]
    public editService = inject(EditService);
    public editDataService = inject(EditDataService);
    public templateEditService = inject(TemplateEditService);
    /** Modo IA: sem barra de formatação (não há folha para formatar) e a prévia sempre aberta. */
    public editModeService = inject(EditModeService);
    public aiEditStore = inject(AiEditStore);
    private _pdfPrintService = inject(PdfPrintService);
    private _companyController = inject(CompanyController);
    private _router = inject(Router);
    private _activatedRoute = inject(ActivatedRoute);
    private _documentSessionController = inject(DocumentSectionController);
    private _toastService = inject(RaToastService);
    private _translate = inject(TranslateService);
    private _raDialogConfirmationService = inject(RaDialogConfirmationService);
    private _guidedTourService = inject(GuidedTourService);
    private _fieldRemovalService = inject(FieldRemovalService);

    /**
     * `true` para documentos que ainda têm um PDF guardado no servidor, gerados antes de
     * o PDF virar a impressão da prévia. Não se produz mais nenhum arquivo assim, então
     * isto é resgate de legado: enquanto o arquivo existir, "Baixar PDF" aparece ao lado
     * de "Gerar PDF" — e some sozinho na primeira edição, que zera o `pdfURL` na API.
     */
    public pdfCompleted = computed(
        () =>
            this.editDataService.document()?.pdfStatus === DocGenPdfStatusEnum.COMPLETED &&
            !!this.editDataService.document()?.pdfURL
    );

    /** `true` quando o documento está em modo checklist. */
    public isChecklistMode = computed(
        () => this.editDataService.document()?.config?.checklistMode ?? false
    );

    /** As seções como etapas da trilha ("Etapa 2 de 3"), para a pílula do header. */
    public checklistSteps = computed(() =>
        computeChecklistSteps(this.editDataService.document()?.sessions)
    );

    /**
     * Abre a lixeira. A lixeira lista o que o SERVIDOR já removeu, então uma
     * remoção ainda em janela de desfazer precisa ser confirmada antes — senão
     * o campo recém-removido não apareceria lá.
     */
    public openTrash(): void {
        this._fieldRemovalService
            .flush()
            .subscribe({ next: () => this.showModalListTrash.set(true) });
    }

    /**
     * Gera o PDF: imprime a prévia. Confirma antes uma remoção pendente de desfazer,
     * senão o PDF sai com o campo que o usuário acabou de remover.
     */
    public generatePdf(): void {
        const document = this.editDataService.document();
        if (!document?._id) return;

        // O PDF é a impressão da própria prévia, que já está paginada no painel (montado
        // mesmo fechado). Quem imprime é ele; aqui só se pede. O flush continua vindo
        // antes: um campo removido na janela de desfazer ainda está na folha.
        this._fieldRemovalService.flush().subscribe({
            next: () => this._pdfPrintService.request(),
        });
    }

    /**
     * Liga/desliga o modo checklist. Ao ligar, expande a lista de seções para revelar
     * os checkboxes e a barra de progresso.
     */
    public onToggleChecklist(): void {
        const next = !this.isChecklistMode();
        this.editService.toggleChecklistMode(next);
        if (next && this.editDataService.sessionsCollapsed()) {
            this.editDataService.toggleSessionsCollapsed();
        }
    }

    constructor() {
        // Assim que o documento clonado carrega (?cloned=true na URL), agenda o
        // tour guiado explicando a troca (aponta pro título ainda estático, só
        // entra em edição DEPOIS que o tour fecha, ver `ngAfterViewChecked`).
        // `_clonedTourChecked` garante que só roda uma vez, na primeira carga.
        effect(() => {
            const doc = this.editDataService.document();
            if (!doc || this._clonedTourChecked) return;
            this._clonedTourChecked = true;

            const cloned = this._activatedRoute.snapshot.queryParams['cloned'] === 'true';
            if (!cloned) return;

            untracked(() => {
                // O parâmetro sai da URL na hora: ficando, recarregar, voltar pelo
                // histórico ou reabrir a aba mostrava o tour de novo a cada entrada.
                this._router.navigate([], {
                    relativeTo: this._activatedRoute,
                    queryParams: { cloned: null },
                    queryParamsHandling: 'merge',
                    replaceUrl: true,
                });

                // E uma vez por documento, mesmo que um link antigo ainda traga o parâmetro.
                if (this._clonedTourSeen(doc._id)) return;
                this._shouldShowClonedTour.set(true);
            });
        });
    }

    /** Já mostrou o tour deste clone? Marca na primeira vez (sem storage, mostra de novo). */
    private _clonedTourSeen(documentId: string): boolean {
        const key = `clonedTourSeen:${documentId}`;
        try {
            if (localStorage.getItem(key)) return true;
            localStorage.setItem(key, '1');
        } catch {
            // Storage bloqueado (aba anônima, política do navegador): segue só pela URL.
        }
        return false;
    }

    /**
     * Volta para a plataforma (remove o foco na seção antes de sair)
     */
    public backPage(): void {
        // Em modo modelo o destino é a lista de modelos, e o rascunho é sincronizado com o
        // modelo ANTES de navegar — senão a lista abriria com o conteúdo antigo.
        const isTemplateMode = this.templateEditService.isTemplateMode();
        const destination = isTemplateMode ? '/platform/templates' : '/platform';

        if (this.leaving()) return;
        this.leaving.set(true);
        // Sincronizar o modelo pode demorar: a tela diz o que está acontecendo.
        if (isTemplateMode) this.editService.initLoading('document.edit.header.savingTemplate');

        this._documentSessionController
            .removeFocusInSection(this.editDataService.getDocumentId())
            .pipe(
                // Soltar o foco é cortesia com os outros usuários: se falhar, sai assim mesmo.
                // Antes o erro interrompia a cadeia e o voltar não fazia nada, prendendo a
                // pessoa no editor.
                catchError(() => of(null)),
                switchMap(() =>
                    isTemplateMode ? this.templateEditService.syncOnLeave() : of(true)
                ),
                finalize(() => {
                    this.leaving.set(false);
                    if (isTemplateMode) this.editService.stopLoading();
                })
            )
            .subscribe({
                next: () => this._router.navigate([destination]),
            });
    }

    /**
     * Fecha o seletor de order e move o documento pro order escolhido.
     */
    public onMoveDocument(order: OrderEntity): void {
        this.showModalMoveDocument.set(false);
        this.editService.moveDocument(order._id);
    }

    /**
     * Inicia a edição do título
     */
    public startEditingTitle(): void {
        const currentTitle = this.editDataService.document()?.title || '';
        this.tempTitle.set(currentTitle);
        this.isEditingTitle.set(true);
        this.shouldFocusInput.set(true);
    }

    ngAfterViewChecked(): void {
        if (this.shouldFocusInput() && this.titleInputRef) {
            this.titleInputRef.nativeElement.focus();
            this.titleInputRef.nativeElement.select();
            this.shouldFocusInput.set(false);
        }

        // Tour pós-clone: aponta pro título AINDA estático (não editável). Só
        // entra em modo de edição (com foco) quando o tour fechar — evita a
        // corrida em que o driver.js manipula o DOM e "borra" (blur) um input
        // recém-focado, disparando saveTitle() e saindo da edição sozinho.
        if (this._shouldShowClonedTour()) {
            this._shouldShowClonedTour.set(false);
            this._guidedTourService.show({
                steps: [
                    {
                        target: '#edit-doc-title-static',
                        title: this._translate.instant('document.edit.header.clonedTourTitle'),
                        description: this._translate.instant(
                            'document.edit.header.clonedTourDescription'
                        ),
                    },
                ],
                onClose: () => this.startEditingTitle(),
            });
        }
    }

    /**
     * Salva o título editado
     */
    public saveTitle(): void {
        const title = this.tempTitle().trim();
        if (title) this.editService.saveNewTitle(title);

        this.isEditingTitle.set(false);
    }

    /**
     * Cancela a edição do título
     */
    public cancelEditingTitle(): void {
        this.isEditingTitle.set(false);
        this.tempTitle.set('');
    }

    /**
     * Handler para o evento Enter no input
     */
    public onTitleKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.saveTitle();
        } else if (event.key === 'Escape') {
            event.preventDefault();
            this.cancelEditingTitle();
        }
    }

    /**
     * Cria um template
     */
    public createTemplate(description: string): void {
        this.isCreatingTemplate.set(false);
    }

    /**
     * Retorna a fonte do documento (documento ou company)
     */
    public getDocumentFontFamily(): string {
        const doc = this.editDataService.document();
        const docFont = doc?.config?.fontFamily;
        if (docFont) return docFont;

        const companyFont =
            this._companyController.currentCompany()?.documentConfig?.configs?.fontFamily;
        return companyFont || environment.DEFAULT_FONT_FAMILY;
    }

    /**
     * Config para o modal — `computed` para manter referência estável entre ciclos de CD.
     * Se fosse um método no template, um objeto novo a cada CD reativaria o `effect` do modal e resetava o form (switches “travados”).
     */
    public documentConfigForModal = computed<DocumentConfigPartial>(() => {
        this.editDataService.document();
        this._companyController.currentCompany();
        const c = this.editDataService.document()?.config;
        // Espalha a config INTEIRA do documento: o modal usa este objeto tanto para
        // semear o form quanto como base do payload de save (a API troca a config
        // toda). Listar campo a campo apagava o que não estivesse na lista — era
        // assim que `sectionPageBreak: false` (e lineSpacing/fontSize/quoteLayout/
        // showVideo/pageMargins/checklistMode) voltava ao default no reload.
        return {
            ...c,
            showCover: c?.showCover ?? true,
            showSummary: c?.showSummary ?? true,
            showHeader: c?.showHeader ?? true,
            showFooter: c?.showFooter ?? true,
            fontFamily: c?.fontFamily ?? this.getDocumentFontFamily(),
            locale: c?.locale ?? 'pt-BR',
        };
    });

    /**
     * Salva as configurações do documento
     */
    public onSaveDocumentConfig(config: DocumentConfigFormValue): void {
        // Auto-save (debounce, sem toast): persiste sem fechar o modal. O usuário fecha no X.
        // Aplicado localmente antes da resposta: os cards atrás do modal precisam
        // refletir número/data/grid na hora, não depois do ida-e-volta.
        const previous = this.editDataService.document()?.config;
        this.editDataService.applyDocumentConfig(config);

        const obs = this.editService.saveDocumentConfig(config, false);
        if (!obs) return;

        this.configHasChanges.set(true);
        this.loadingDocumentConfig.set(true);
        this.editDataService.beginSave();
        obs.subscribe({
            complete: () => {
                this.loadingDocumentConfig.set(false);
                this.editDataService.endSave();
            },
            error: () => {
                this.loadingDocumentConfig.set(false);
                this.editDataService.applyDocumentConfig(previous);
                this.editDataService.endSave(false);
            },
        });
    }

    /**
     * Usuário trocou o grid de imagens. O autosave já persistiu o novo default (grupos
     * novos). Se houver grupos de imagens já existentes, pergunta se aplica a todos eles.
     */
    public onApplyGridToAllGroups(grid: number): void {
        const doc = this.editDataService.document();
        const hasImageGroups = (doc?.sessions ?? []).some(session =>
            (session.items ?? []).some(item => item.type === DocumentItemTypeEnum.GROUP_IMAGE)
        );
        // Sem grupos existentes não há o que aplicar — o default já cobre os novos.
        if (!hasImageGroups) return;

        this._raDialogConfirmationService
            .confirm({
                title: this._translate.instant('document.edit.documentConfig.applyGridTitle'),
                message: this._translate.instant('document.edit.documentConfig.applyGridMessage', {
                    grid,
                }),
                confirmText: this._translate.instant(
                    'document.edit.documentConfig.applyGridConfirm'
                ),
                cancelText: this._translate.instant('document.edit.documentConfig.applyGridCancel'),
            })
            .subscribe({
                next: dialogControl => {
                    dialogControl.setLoading(true);
                    this.editService.applyGridToAllGroups(grid).subscribe({
                        next: () => dialogControl.close(),
                        error: () => dialogControl.setLoading(false),
                    });
                },
            });
    }

    /**
     * Abre o editor de conteúdo do rodapé, semeando o footer default se ainda não existir.
     */
    public onEditFooter(): void {
        // Evita reentrância (duplo-clique) enquanto o editor já está aberto/abrindo.
        if (this.showFooterEditor() || this.openingEditor()) return;
        this.openingEditor.set('footer');
        this.editService
            .seedFooterIfEmpty()
            .pipe(finalize(() => this.openingEditor.set(null)))
            .subscribe({
                next: () => this.showFooterEditor.set(true),
                // O controller já avisa o erro; aqui só destrava o botão (finalize).
                error: error => console.error(error),
            });
    }

    /**
     * Salva o conteúdo do rodapé. Ao concluir, fecha o footer COM animação e volta
     * para as definições.
     */
    public onSaveFooter(event: FooterEditorSaveEvent): void {
        const obs = this.editService.saveFooter(event.footer);
        if (!obs) return;

        this.loadingFooter.set(true);
        obs.subscribe({
            next: () => {
                this.loadingFooter.set(false);
                this.configHasChanges.set(true);
                // Fecha só este editor e volta para as definições: é lá que o usuário
                // escolhe o alcance das alterações, no botão Salvar.
                const ref = this.footerEditorRef();
                if (ref) ref.animateClose();
                else this.showFooterEditor.set(false);
            },
            error: () => this.loadingFooter.set(false),
        });
    }

    /**
     * Chamado quando o modal do footer termina de fechar (X ou após salvar). Nos dois
     * casos volta para as definições, onde fica o botão Salvar.
     */
    public onFooterClosed(): void {
        this.showFooterEditor.set(false);
    }

    /**
     * Fecha o settings; se o footer/header/capa estiver aberto (caso de borda), fecha junto.
     */
    public onConfigClosed(): void {
        // Sem alterações, sai direto. Com alterações, confirma que elas valerão só para
        // este documento — que é justamente o que o auto-save já gravou.
        if (!this.configHasChanges()) {
            this._closeConfigModals();
            return;
        }

        // `confirm` emite só na confirmação e completa nos dois casos, então o flag
        // distingue "cancelou" de "confirmou" no `complete`.
        let confirmed = false;

        this._raDialogConfirmationService
            .confirm({
                title: this._translate.instant(
                    'document.edit.documentConfig.leaveWithoutSaveTitle'
                ),
                message: this._translate.instant(
                    'document.edit.documentConfig.leaveWithoutSaveMessage'
                ),
            })
            .subscribe({
                next: dialogControl => {
                    confirmed = true;
                    dialogControl.close();
                    this._closeConfigModals();
                },
                // Cancelou: o modal de config já rodou a animação de saída (o `close`
                // é emitido depois dela), então precisa ser trazido de volta — senão
                // fica montado e invisível, com a tela presa no fundo escurecido.
                complete: () => {
                    if (!confirmed) this.configModalRef()?.cancelClose();
                },
            });
    }

    /** Fecha o settings e, por segurança, qualquer editor de conteúdo aberto por cima. */
    private _closeConfigModals(): void {
        this.isModalDocumentConfigVisible.set(false);
        this.isModalConfigScopeVisible.set(false);
        this.showFooterEditor.set(false);
        this.showHeaderEditor.set(false);
        this.showCoverEditor.set(false);
        this.configHasChanges.set(false);
    }

    /**
     * Salvar não persiste nada de novo: as alterações já foram gravadas no documento
     * durante a edição. Abre o modal que pergunta o alcance.
     */
    public onSaveConfigScope(): void {
        this.isModalConfigScopeVisible.set(true);
    }

    /** Aplica o alcance escolhido. "Só este documento" não precisa ir à API. */
    public onConfigScopeSelected(scope: DocumentConfigScopeEnum): void {
        if (scope === DocumentConfigScopeEnum.DOCUMENT) {
            this._closeConfigModals();
            return;
        }

        this.savingConfigScope.set(true);
        this.editService.saveConfigScope(scope).subscribe({
            next: () => {
                this.savingConfigScope.set(false);
                this._closeConfigModals();
            },
            error: () => this.savingConfigScope.set(false),
        });
    }

    /**
     * Abre o editor de conteúdo do cabeçalho, semeando o header default se não existir.
     */
    public onEditHeader(): void {
        if (this.showHeaderEditor() || this.openingEditor()) return;
        this.openingEditor.set('header');
        this.editService
            .seedHeaderIfEmpty()
            .pipe(finalize(() => this.openingEditor.set(null)))
            .subscribe({
                next: () => this.showHeaderEditor.set(true),
                // O controller já avisa o erro; aqui só destrava o botão (finalize).
                error: error => console.error(error),
            });
    }

    /**
     * Salva o conteúdo do cabeçalho. Ao concluir, fecha o header COM animação e volta
     * para as definições.
     */
    public onSaveHeader(event: HeaderEditorSaveEvent): void {
        const obs = this.editService.saveHeader(event.header);
        if (!obs) return;

        this.loadingHeader.set(true);
        obs.subscribe({
            next: () => {
                this.loadingHeader.set(false);
                this.configHasChanges.set(true);
                // Fecha só este editor e volta para as definições: é lá que o usuário
                // escolhe o alcance das alterações, no botão Salvar.
                const ref = this.headerEditorRef();
                if (ref) ref.animateClose();
                else this.showHeaderEditor.set(false);
            },
            error: () => this.loadingHeader.set(false),
        });
    }

    /**
     * Chamado quando o modal do header termina de fechar (X ou após salvar).
     */
    public onHeaderClosed(): void {
        this.showHeaderEditor.set(false);
    }

    /**
     * Abre o editor de conteúdo da capa. A capa é baseada em template e sempre existe,
     * então o editor se auto-carrega (não há "seed se vazio" como no footer/header).
     */
    public onEditCover(): void {
        if (this.showCoverEditor()) return;
        this.showCoverEditor.set(true);
    }

    /**
     * Salva o conteúdo da capa. Ao concluir, fecha a capa COM animação e volta para
     * as definições.
     */
    public onSaveCover(event: CoverEditorSaveEvent): void {
        const obs = this.editService.saveCover(event.cover);
        if (!obs) return;

        this.loadingCover.set(true);
        obs.subscribe({
            next: () => {
                this.loadingCover.set(false);
                this.configHasChanges.set(true);
                // Fecha só este editor e volta para as definições: é lá que o usuário
                // escolhe o alcance das alterações, no botão Salvar.
                const ref = this.coverEditorRef();
                if (ref) ref.animateClose();
                else this.showCoverEditor.set(false);
            },
            error: () => this.loadingCover.set(false),
        });
    }

    /**
     * Chamado quando o modal da capa termina de fechar (X ou após salvar).
     */
    public onCoverClosed(): void {
        this.showCoverEditor.set(false);
    }

    /**
     * Baixa o PDF já gerado. O arquivo mora no CDN (outra origem), onde o atributo
     * `download` de um `<a>` é ignorado — por isso salvamos via `blob:` URL
     * (`file-download.util`), com fallback de abrir em nova aba. Espelha o preview.
     */
    public downloadPDF(): void {
        if (this.downloading()) return;

        const pdfURL = this.editDataService.document()?.pdfURL;
        if (!pdfURL) {
            this._toastService.error({
                title: this._translate.instant('document.preview.pdfDownloadErrorTitle'),
                message: this._translate.instant('document.preview.pdfDownloadErrorMessage'),
            });
            return;
        }

        const fileName = pdfFileNameFromTitle(this.editDataService.document()?.title, pdfURL);

        // O download baixa o arquivo inteiro antes de salvar: o loading do cabeçalho avisa.
        this.downloading.set(true);
        void downloadFileFromUrl(pdfURL, fileName)
            .then(downloaded => {
                if (!downloaded) openFileInNewTab(pdfURL);
            })
            .finally(() => this.downloading.set(false));
    }

    /**
     * Salva os signatários do contrato
     */
    public onSaveContractSigners(signers: SignedByPartial[]): void {
        const obs = this.editService.saveDocumentSigners(signers);
        if (!obs) return;

        this.loadingContractSigners.set(true);
        obs.subscribe({
            next: () => {
                this.loadingContractSigners.set(false);
                this.showModalContractSigners.set(false);
            },
            error: () => this.loadingContractSigners.set(false),
            complete: () => this.loadingContractSigners.set(false),
        });
    }

    /**
     * Liga/desliga o modo privado do documento (toggle do modal de configurações).
     * O backend popula `sharedWith` ao tornar privado; o doc local é atualizado.
     */
    public onVisibilityChange(isPrivate: boolean): void {
        const obs = this.editService.updateVisibility(isPrivate);
        if (!obs) return;

        this.loadingVisibility.set(true);
        obs.subscribe({
            next: () => this.loadingVisibility.set(false),
            error: () => this.loadingVisibility.set(false),
            complete: () => this.loadingVisibility.set(false),
        });
    }

    /**
     * Abre "Quem pode acessar" trocando de modal: fecha a visibilidade e abre o acesso
     * (um sai, o outro entra — sem empilhar modais).
     */
    public openAccessList(): void {
        this.showModalDocumentVisibility.set(false);
        this.showModalDocumentAccess.set(true);
    }

    /**
     * Fecha "Quem pode acessar" e volta para a visibilidade.
     */
    public closeAccessList(): void {
        this.showModalDocumentAccess.set(false);
        this.showModalDocumentVisibility.set(true);
    }

    /**
     * Salva a lista "Quem pode acessar" (sharedWith) e volta para a visibilidade.
     */
    public onSaveDocumentAccess(sharedWith: SharedWithPartial[]): void {
        const obs = this.editService.updateSharedWith(sharedWith);
        if (!obs) return;

        this.loadingDocumentAccess.set(true);
        obs.subscribe({
            next: () => {
                this.loadingDocumentAccess.set(false);
                this.closeAccessList();
            },
            error: () => this.loadingDocumentAccess.set(false),
            complete: () => this.loadingDocumentAccess.set(false),
        });
    }
}
